import angular, {
  Injectable,
  IPromise,
  IQService,
  IRequestConfig,
  ui,
} from "angular";
import { IFieldArray } from "AngularFormly";
import { Record } from "../../server/crud";
import { IHasResponseStatus } from "../../server/query";
import { ServerUtility } from "../../server/serverUtility";
import { FormSubmitArgs, FormViewController } from "./controller";
import { GridConfiguration } from "../../views/recordGridView";
import { IInjectable, StateService } from "@uirouter/angularjs";

export const vsServiceName = "formViewService";
export const vsName = vsServiceName;

export interface SubmitFormConfig extends SaveFormConfig {
  method: string;
}

export interface SaveFormConfig {
  url: string;
  formObject: unknown;
  formViewController: FormViewController;
  validationPrefix?: string;
}

export interface FormViewServiceFormFactory {
  (...args: any[]): FormViewConfig;
}

/**
 * @argument submitForm - an injectable that constructs an injectable.  The inner injectable is due to an & binding and can inject model and formViewController
 */
export interface FormViewConfig {
  name: string;
  fields: IFieldArray;
  title: (model: any) => string;
  submitForm?: (args: FormSubmitArgs) => IPromise<any>;
  showButtonsAtTop?: boolean;
}

export interface FormViewInstanceConfig {}

export class FormViewService {
  constructor(
    private readonly configs: {
      [name: string]: Injectable<FormViewServiceFormFactory> | undefined;
    },
    private readonly serverUtility: ServerUtility,
    private readonly $injector: angular.auto.IInjectorService
  ) {}

  getFormViewConfig<TConfig extends FormViewInstanceConfig>(
    name: string,
    config?: TConfig
  ): FormViewConfig | undefined {
    const configFactory = this.configs[name];
    if (configFactory === undefined) return undefined;

    return this.$injector.invoke(configFactory, undefined, { config });
  }

  saveForm(config: SaveFormConfig) {
    const id = (<Record>config.formObject)?.Id;

    const newItem = id === undefined || id === 0;
    const method = newItem ? "POST" : "PUT";

    const url = newItem ? config.url : `${config.url}/${id}`;

    return this.submitForm({
      formObject: config.formObject,
      formViewController: config.formViewController,
      method,
      url,
      validationPrefix: config.validationPrefix,
    });
  }

  submitForm<T>(config: SubmitFormConfig) {
    const requestConfig: IRequestConfig = {
      method: config.method,
      url: config.url,
      data: config.formObject,
    };

    return this.serverUtility
      .sendApiRequest<T>(requestConfig)
      .catch((error) => {
        const apiResponse = <IHasResponseStatus>error.data;
        config.formViewController.setApiErrors(
          apiResponse.ResponseStatus,
          config.validationPrefix
        );

        throw error;
      });
  }

  static getResolveObjectFromFormViewConfig(
    formViewConfigFactory: Injectable<(...args: any[]) => FormViewConfig>,
    modelFactory: Injectable<(...args: any[]) => any>,
    cancelAction?: Injectable<
      (
        ...args: any[]
      ) => Injectable<(submitArgs: FormSubmitArgs) => IPromise<any>>
    >
  ): {
    [key: string]: IInjectable;
  } {
    return {
      formViewConfig: formViewConfigFactory,
      model: modelFactory,
      fields: [
        "formViewConfig",
        (formViewConfig: FormViewConfig) => formViewConfig.fields,
      ],
      title: [
        "formViewConfig",
        "model",
        (formViewConfig: FormViewConfig, model: any) =>
          formViewConfig.title(model),
      ],
      onSubmit: [
        "formViewConfig",
        "$q",
        (formViewConfig: FormViewConfig, $q: IQService) => [
          "formViewController",
          "model",
          (formViewController: FormViewController, model: any) =>
            formViewConfig.submitForm
              ? formViewConfig.submitForm({ formViewController, model })
              : $q.when(),
        ],
      ],
      onCancel: cancelAction ?? [
        "$state",
        ($state: StateService) => [() => $state.go("^")],
      ],
      showButtonsAtTop: [
        "formViewConfig",
        (formViewConfig: FormViewConfig) => !!formViewConfig.showButtonsAtTop,
      ],
    };
  }
}
